<template>
  <transition name="contact-modal">
    <div
      v-if="open"
      class="contact-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
      aria-describedby="contact-modal-description"
      @keydown.esc="$emit('close')"
    >
      <button
        class="contact-modal__backdrop"
        type="button"
        aria-label="Kontaktformular schliessen"
        @click="$emit('close')"
      ></button>

      <section ref="panel" class="contact-modal__panel" tabindex="-1">
        <div class="contact-modal__top">
          <div>
            <p class="section-kicker">Kontakt</p>

            <h2 id="contact-modal-title">
              Projekt starten.
            </h2>

            <p id="contact-modal-description">
              Für Shootings, Events, Hochzeiten, Portraits, Sport, Content oder eine Idee,
              die noch keinen Namen hat.
            </p>
          </div>

          <button
            class="contact-modal__close"
            type="button"
            aria-label="Kontaktformular schliessen"
            @click="$emit('close')"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <form
          class="contact-form"
          action="https://formspree.io/f/mldjwjyv"
          method="POST"
          novalidate
          @submit.prevent="handleSubmit"
        >
          <div class="form-grid">
            <div class="form-group">
              <label for="contact-name">Name</label>

              <input
                id="contact-name"
                ref="firstInput"
                v-model="formData.name"
                type="text"
                name="name"
                autocomplete="name"
                placeholder="Dein Name"
                :class="{ error: errors.name }"
                :aria-invalid="Boolean(errors.name)"
                :aria-describedby="errors.name ? 'contact-name-error' : undefined"
                @input="clearError('name')"
              >

              <span
                v-if="errors.name"
                id="contact-name-error"
                class="field-error"
              >
                {{ errors.name }}
              </span>
            </div>

            <div class="form-group">
              <label for="contact-email">E-Mail</label>

              <input
                id="contact-email"
                v-model="formData.email"
                type="email"
                name="email"
                autocomplete="email"
                inputmode="email"
                placeholder="name@beispiel.ch"
                :class="{ error: errors.email }"
                :aria-invalid="Boolean(errors.email)"
                :aria-describedby="errors.email ? 'contact-email-error' : undefined"
                @input="clearError('email')"
              >

              <span
                v-if="errors.email"
                id="contact-email-error"
                class="field-error"
              >
                {{ errors.email }}
              </span>
            </div>
          </div>

          <div class="form-group">
            <label for="contact-subject">Betreff</label>

            <input
              id="contact-subject"
              v-model="formData.subject"
              type="text"
              name="subject"
              autocomplete="off"
              placeholder="Worum geht es?"
              :class="{ error: errors.subject }"
              :aria-invalid="Boolean(errors.subject)"
              :aria-describedby="errors.subject ? 'contact-subject-error' : undefined"
              @input="clearError('subject')"
            >

            <span
              v-if="errors.subject"
              id="contact-subject-error"
              class="field-error"
            >
              {{ errors.subject }}
            </span>
          </div>

          <div class="form-group">
            <label for="contact-message">Nachricht</label>

            <textarea
              id="contact-message"
              v-model="formData.message"
              name="message"
              placeholder="Erzähl mir kurz von deinem Projekt."
              :class="{ error: errors.message }"
              :aria-invalid="Boolean(errors.message)"
              :aria-describedby="errors.message ? 'contact-message-error' : undefined"
              @input="clearError('message')"
            ></textarea>

            <span
              v-if="errors.message"
              id="contact-message-error"
              class="field-error"
            >
              {{ errors.message }}
            </span>
          </div>

          <button
            type="submit"
            class="contact-submit"
            :disabled="isSubmitting"
          >
            <span
              v-if="isSubmitting"
              class="contact-submit__spinner"
              aria-hidden="true"
            ></span>

            <span>
              {{ isSubmitting ? 'Nachricht unterwegs …' : 'Nachricht senden' }}
            </span>
          </button>

          <transition name="form-message">
            <div
              v-if="submitSuccess"
              class="form-status form-status--success"
              role="status"
              aria-live="polite"
            >
              Vielen Dank! Deine Nachricht wurde erfolgreich gesendet.
            </div>
          </transition>

          <transition name="form-message">
            <div
              v-if="submitError"
              class="form-status form-status--error"
              role="alert"
            >
              Ein Fehler ist aufgetreten. Bitte versuche es erneut oder schreibe mir direkt per Mail.
            </div>
          </transition>
        </form>
      </section>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ContactModal',

  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close'],

  data() {
    return {
      formData: {
        name: '',
        email: '',
        subject: '',
        message: '',
      },
      errors: {},
      isSubmitting: false,
      submitSuccess: false,
      submitError: false,
      previouslyFocusedElement: null,
    }
  },

  watch: {
    open: {
      immediate: true,
      handler(value) {
        if (typeof document === 'undefined') return

        if (value) {
          this.previouslyFocusedElement = document.activeElement
          document.body.style.overflow = 'hidden'
          document.addEventListener('keydown', this.handleKeydown)

          this.$nextTick(() => {
            this.$refs.firstInput?.focus()
          })
        } else {
          document.body.style.overflow = ''
          document.removeEventListener('keydown', this.handleKeydown)

          this.$nextTick(() => {
            this.previouslyFocusedElement?.focus?.()
          })
        }
      },
    },
  },

  beforeUnmount() {
    if (typeof document === 'undefined') return

    document.body.style.overflow = ''
    document.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('close')
      }
    },

    clearError(field) {
      if (!this.errors[field]) return

      const nextErrors = { ...this.errors }
      delete nextErrors[field]
      this.errors = nextErrors
    },

    validateForm() {
      const errors = {}

      if (!this.formData.name.trim()) {
        errors.name = 'Name ist erforderlich'
      }

      if (!this.formData.email.trim()) {
        errors.email = 'E-Mail ist erforderlich'
      } else if (!this.validateEmail(this.formData.email)) {
        errors.email = 'Bitte gib eine gültige E-Mail-Adresse ein'
      }

      if (!this.formData.subject.trim()) {
        errors.subject = 'Betreff ist erforderlich'
      }

      if (!this.formData.message.trim()) {
        errors.message = 'Nachricht ist erforderlich'
      }

      this.errors = errors
      return Object.keys(errors).length === 0
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },

    resetForm() {
      this.formData = {
        name: '',
        email: '',
        subject: '',
        message: '',
      }
      this.errors = {}
    },

    async handleSubmit(event) {
      if (!this.validateForm() || this.isSubmitting) return

      this.isSubmitting = true
      this.submitError = false
      this.submitSuccess = false

      try {
        const form = event.currentTarget
        const formData = new FormData(form)

        const response = await fetch(form.action, {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/json',
          },
        })

        if (!response.ok) {
          throw new Error(`Form submission failed with status ${response.status}`)
        }

        this.submitSuccess = true
        this.resetForm()

        window.setTimeout(() => {
          this.submitSuccess = false
          this.$emit('close')
        }, 1800)
      } catch (error) {
        console.error('Contact form error:', error)
        this.submitError = true
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
/* LOGO-BLAU: #373D6B · RGB 55, 61, 107 */
.contact-modal {
  --navy-950: #171a31;
  --navy-900: #242a4c;
  --navy-800: #303760;
  --blue-600: #373d6b;
  --blue-500: #626c9f;
  --blue-100: #eceef6;
  --surface: #ffffff;
  --surface-soft: #f7f7fa;
  --border: rgba(55, 61, 107, 0.14);
  --text-primary: #1b203a;
  --text-secondary: #5f7088;
  --danger: #c43f54;
  --success: #137a52;

  position: fixed;
  inset: 0;
  z-index: 600;
  display: grid;
  place-items: center;
  padding: 24px;
  color: var(--text-primary);
  font-family: 'Halenoir', sans-serif;
}

.contact-modal__backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background:
    radial-gradient(circle at 18% 8%, rgba(98, 108, 159, 0.2), transparent 34%),
    rgba(23, 26, 49, 0.76);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  cursor: pointer;
}

.contact-modal__panel {
  position: relative;
  z-index: 1;
  width: min(100%, 880px);
  max-height: min(90vh, 940px);
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(55, 61, 107, 0.32) transparent;
  border: 1px solid rgba(255, 255, 255, 0.74);
  border-radius: 34px;
  background:
    radial-gradient(circle at 100% 0%, rgba(98, 108, 159, 0.1), transparent 36%),
    linear-gradient(180deg, #ffffff 0%, #fafafe 100%);
  box-shadow:
    0 40px 120px rgba(23, 26, 49, 0.34),
    0 2px 8px rgba(23, 26, 49, 0.08);
  outline: none;
  padding: 44px;
}

.contact-modal__panel::-webkit-scrollbar {
  width: 10px;
}

.contact-modal__panel::-webkit-scrollbar-thumb {
  border: 3px solid transparent;
  border-radius: 999px;
  background: rgba(55, 61, 107, 0.28);
  background-clip: padding-box;
}

.contact-modal__top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 32px;
  margin-bottom: 36px;
}

.section-kicker {
  color: var(--blue-600);
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.24em;
  margin: 0 0 16px;
  text-transform: uppercase;
}

.contact-modal__top h2 {
  max-width: 700px;
  color: var(--navy-950);
  font-size: clamp(3rem, 6.5vw, 6rem);
  font-weight: 900;
  letter-spacing: -0.07em;
  line-height: 0.92;
  margin: 0;
  text-wrap: balance;
}

.contact-modal__top p:not(.section-kicker) {
  max-width: 630px;
  color: var(--text-secondary);
  font-size: 1.05rem;
  line-height: 1.75;
  margin: 24px 0 0;
}

.contact-modal__close {
  width: 48px;
  height: 48px;
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
  color: var(--navy-900);
  box-shadow: 0 8px 24px rgba(41, 46, 82, 0.08);
  cursor: pointer;
  font-family: inherit;
  font-size: 2rem;
  line-height: 1;
  transition:
    transform 180ms ease,
    background 180ms ease,
    border-color 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease;
}

.contact-modal__close:hover {
  transform: rotate(8deg) scale(1.04);
  border-color: rgba(55, 61, 107, 0.25);
  background: var(--blue-600);
  color: #ffffff;
  box-shadow: 0 12px 28px rgba(55, 61, 107, 0.22);
}

.contact-modal__close:focus-visible,
.contact-submit:focus-visible,
.form-group input:focus-visible,
.form-group textarea:focus-visible {
  outline: 3px solid rgba(55, 61, 107, 0.22);
  outline-offset: 3px;
}

.contact-form {
  display: grid;
  gap: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.form-group {
  display: grid;
  gap: 9px;
}

.form-group label {
  color: var(--navy-900);
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.form-group input,
.form-group textarea {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 17px;
  background: rgba(247, 247, 250, 0.88);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 1rem;
  outline: none;
  padding: 16px 18px;
  transition:
    border-color 180ms ease,
    background 180ms ease,
    box-shadow 180ms ease,
    transform 180ms ease;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(95, 112, 136, 0.68);
}

.form-group textarea {
  min-height: 180px;
  resize: vertical;
}

.form-group input:hover,
.form-group textarea:hover {
  border-color: rgba(55, 61, 107, 0.24);
  background: #ffffff;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: rgba(55, 61, 107, 0.66);
  background: #ffffff;
  box-shadow:
    0 0 0 4px rgba(55, 61, 107, 0.1),
    0 10px 28px rgba(41, 46, 82, 0.06);
}

.form-group input.error,
.form-group textarea.error {
  border-color: rgba(196, 63, 84, 0.7);
  box-shadow: 0 0 0 4px rgba(196, 63, 84, 0.08);
}

.field-error {
  color: var(--danger);
  font-size: 0.82rem;
  font-weight: 750;
  line-height: 1.4;
}

.contact-submit {
  min-height: 58px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 11px;
  border: 1px solid rgba(83, 137, 255, 0.4);
  border-radius: 999px;
  background: linear-gradient(135deg, var(--blue-500), var(--blue-600));
  color: #ffffff;
  box-shadow:
    0 16px 34px rgba(55, 61, 107, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 900;
  margin-top: 4px;
  padding: 0 28px;
  transition:
    transform 180ms ease,
    border-color 180ms ease,
    box-shadow 180ms ease,
    opacity 180ms ease;
}

.contact-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: rgba(98, 108, 159, 0.7);
  box-shadow:
    0 20px 42px rgba(55, 61, 107, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.contact-submit:active:not(:disabled) {
  transform: translateY(0);
}

.contact-submit:disabled {
  cursor: not-allowed;
  opacity: 0.68;
  transform: none;
}

.contact-submit__spinner {
  width: 17px;
  height: 17px;
  border: 2px solid rgba(255, 255, 255, 0.38);
  border-top-color: #ffffff;
  border-radius: 999px;
  animation: contact-spin 700ms linear infinite;
}

.form-status {
  border-radius: 17px;
  font-weight: 800;
  line-height: 1.5;
  padding: 16px 18px;
}

.form-status--success {
  border: 1px solid rgba(19, 122, 82, 0.2);
  background: rgba(19, 122, 82, 0.08);
  color: #0e6744;
}

.form-status--error {
  border: 1px solid rgba(196, 63, 84, 0.2);
  background: rgba(196, 63, 84, 0.08);
  color: #a32f43;
}

.contact-modal-enter-active,
.contact-modal-leave-active {
  transition: opacity 240ms ease;
}

.contact-modal-enter-active .contact-modal__panel,
.contact-modal-leave-active .contact-modal__panel {
  transition:
    opacity 270ms ease,
    transform 270ms cubic-bezier(0.22, 1, 0.36, 1);
}

.contact-modal-enter-from,
.contact-modal-leave-to {
  opacity: 0;
}

.contact-modal-enter-from .contact-modal__panel,
.contact-modal-leave-to .contact-modal__panel {
  opacity: 0;
  transform: translateY(20px) scale(0.985);
}

.form-message-enter-active,
.form-message-leave-active {
  transition:
    opacity 220ms ease,
    transform 220ms ease;
}

.form-message-enter-from,
.form-message-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@keyframes contact-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 720px) {
  .contact-modal {
    align-items: end;
    padding: 10px;
  }

  .contact-modal__panel {
    width: 100%;
    max-height: 94vh;
    border-radius: 26px 26px 20px 20px;
    padding: 26px 20px 22px;
  }

  .contact-modal__top {
    gap: 18px;
  }

  .contact-modal__top h2 {
    font-size: clamp(2.8rem, 14vw, 4.6rem);
  }

  .contact-modal__top p:not(.section-kicker) {
    font-size: 1rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .contact-modal__close {
    width: 44px;
    height: 44px;
  }
}

@media (max-width: 420px) {
  .contact-modal__top {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .contact-modal__top h2 {
    font-size: clamp(2.45rem, 13vw, 3.6rem);
  }

  .section-kicker {
    letter-spacing: 0.18em;
  }

  .form-group input,
  .form-group textarea {
    border-radius: 15px;
    padding: 15px 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .contact-modal,
  .contact-modal__panel,
  .contact-modal__close,
  .contact-submit,
  .form-group input,
  .form-group textarea,
  .form-message-enter-active,
  .form-message-leave-active {
    transition: none;
  }

  .contact-submit__spinner {
    animation: none;
  }
}
</style>
